import React, { FC, useRef, useState, useCallback, useEffect, useContext } from 'react';

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { getI18nData, isBrowser } from '@lucid/utils';
import { Player, IPlayerProps } from '@lottiefiles/react-lottie-player';
import { AnimationItem } from 'lottie-web';

import { LucidTheme, I18n } from '../../types/types';
import Box from '../box/box';
import Icon from '../icon/icon';
import Link from '../link/link';
import LayoutContext from '../context/layout-context';
import decorate from '../decorate/decorate';

export interface ScrollLottieProps extends Omit<IPlayerProps, 'src' | 'loop'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The lottie animation JSON data. */
  data?: string;
  /** The i18n translation data. */
  i18nData?: I18n.DataEdges;
  /** The path the lottie animation JSON file. */
  path?: string;
  /** The speed at which GSAP scroll trigger should play the lottie animation. */
  scrollSpeed?: 'fast' | 'medium' | 'slow';
  /** The offset from the top where the animation will begin to play. */
  topOffset?: string;
}

const ScrollLottieComponent: FC<ScrollLottieProps> = ({
  autoplay = false,
  className,
  data,
  i18nData,
  path,
  renderer = 'canvas',
  scrollSpeed = 'medium',
  topOffset = 'top',
  ...rest
}) => {
  const {
    language: { i18nKey },
  } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, i18nKey);
  const [isSkipVisible, setIsSkipVisible] = useState(false);
  const [animationData, setAnimationData] = useState<string | object>('');
  const lottieRef = useRef<AnimationItem>();
  const playerRef = useRef<Player>(null);
  const lottieRefs = useCallback(
    node => {
      lottieRef.current = node;
    },
    [lottieRef],
  );
  if (isBrowser()) {
    gsap.registerPlugin(ScrollTrigger);
  }
  useEffect(() => {
    const animationData: string | object = data ? JSON.parse(data) : path;

    setAnimationData(animationData);
  }, [data, path]);

  const initAnimation = () => {
    if (isBrowser()) {
      setIsSkipVisible(false);
      const initialFrame = { frame: 0 };
      const lottieTarget = document.getElementById('lottie');
      const initialTarget = gsap.utils.toArray(lottieTarget)[0] as ArrayLike<string | Element | null>;
      const initialSpeed = {
        slow: '+=12000',
        medium: '+=10000',
        fast: '+=8000',
      };
      const animationScrollTrigger = {
        trigger: initialTarget,
        pin: true,
        start: `top ${topOffset}`,
        end: initialSpeed[scrollSpeed] || initialSpeed.medium,
        scrub: 1,
      };
      const totalFrames = lottieRef.current?.totalFrames || 0;
      playerRef.current?.setSeeker(initialFrame.frame);

      // If the lottieTarget gets taken away by conditional rendering
      if (lottieTarget) {
        gsap.to(initialFrame, {
          frame: totalFrames - 1,
          ease: 'none',
          onUpdate: () => {
            playerRef.current?.setSeeker(initialFrame.frame);
            setIsSkipVisible(initialFrame.frame > (2 / 100) * totalFrames);
          },
          scrollTrigger: animationScrollTrigger,
        });
        // in case there are any other ScrollTriggers on the page and the loading of this Lottie asset caused layout changes
        ScrollTrigger.sort();
        ScrollTrigger.refresh();
      }
    }
  };

  const handleSkip = () => {
    const scrollWrapper = playerRef?.current?.container?.parentElement!;
    const scrollTarget = scrollWrapper.scrollTop + scrollWrapper.offsetHeight + 20;
    if (isBrowser()) window.scrollTo(0, scrollTarget);
  };

  return (
    <>
      <Player
        {...rest}
        ref={playerRef}
        autoplay={autoplay}
        lottieRef={lottieRefs}
        onEvent={event => {
          if (event === 'load') {
            initAnimation();
          }
        }}
        renderer={renderer}
        rendererSettings={{
          filterSize: {
            width: '200%',
            height: '200%',
            x: '-50%',
            y: '-50%',
          },
        }}
        src={animationData}
      />
      <Box
        as="div"
        css={(theme: LucidTheme) => ({
          position: 'sticky',
          bottom: theme.space.s24,
          textAlign: 'right',
          paddingRight: theme.space.s120,
        })}
      >
        <Link
          aria-label={i18n('mktg_skip_animation')}
          css={{
            opacity: isSkipVisible ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            ['&:focus']: {
              opacity: 1,
            },
          }}
          onClick={handleSkip}
          onKeyPress={handleSkip}
          role="link"
          tabIndex={0}
        >
          {i18n('mktg_skip')}
          <Icon
            css={(theme: LucidTheme) => ({
              top: '3px',
              margin: `0 ${theme.space.s8}px`,
            })}
            type="CarotDown"
          />
        </Link>
      </Box>
    </>
  );
};

// manually adding display name or it goes missing on build
ScrollLottieComponent.displayName = 'ScrollLottie';
const ScrollLottie = decorate<ScrollLottieProps>(ScrollLottieComponent);

export { ScrollLottie };
export default ScrollLottie;
